/**
 * What a host holds about one attempt, and the facts derived from it.
 *
 * No DOM and no I/O. Every function below answers a question about a record and nothing else, so
 * the studio and the player — the two hosts this repository writes — answer it the same way. They
 * used to be one host; the reason this file exists is that a second must not drift from the first.
 */

import { solutionFor } from '@cet/asset-scoring';
import type {
  FeedbackMode,
  HintPayload,
  ItemResponse,
  ItemVerdict,
  Judgement,
  Mode,
  SchemeDoc,
  Score,
} from '@cet/asset-contract';

/**
 * The host's record of an attempt, and the three settings it is played under.
 *
 * A real host keeps this in a database; the studio and the player keep it in a page, which is the
 * one honest caveat in both. Neither stores it anywhere (invariant 3).
 */
export interface HostRecord {
  mode: Mode;
  feedbackMode: FeedbackMode;
  locale: string;
  state: unknown;
  responses: Record<string, ItemResponse>;
  /**
   * The AI's judgement of each open answer (D93), kept beside the responses so the submit scores
   * the answer the learner was marked on — each for the text it judged, and no other.
   */
  judgements: Record<string, Judgement>;
  /** Every hint the learner asked for, in order (D48). Empty in exam, and that is the check. */
  hintsUsed: HintPayload[];
  /**
   * How many times each question was checked — marked for the learner in this session (D66). The
   * host's own count, because it is what decides whether a solution request is honoured.
   */
  checks: Record<string, number>;
  /** The questions whose answer the learner asked to see, in order (D66). */
  solutionsShown: string[];
  /**
   * The mark each question was last given in this session — as its answer arrived (`onAnswer`) or
   * at a Check (`onCheck`) — with the locale its message is in. The asset was already shown these,
   * so a re-mount hands them back in `init.verdicts` and a resumed question shows its mark (D70).
   * A new answer to the question drops its mark: it was for the answer before.
   */
  released: Record<string, { verdict: ItemVerdict; locale: string }>;
  verdicts: ItemVerdict[] | null;
  /** Which locale `verdicts[].message` was resolved in — see `verdictsForInit`. */
  verdictsLocale: string | null;
  score: Score | null;
  serverScore: Score | null;
  /** Each part's score from the same submit, when the scheme names parts (D93). */
  serverParts: Record<string, Score> | null;
  browserScore: Score | null;
  /**
   * Whether this attempt has been submitted, which is what makes releasing the marks safe (D49).
   *
   * A submit is the end of the attempt, not a scoring button pressed mid-flight. Until this
   * existed the studio had no way to say so: `canWrite` was spent on `review` alone, so a learner
   * could submit, be shown which items were wrong, change them and submit again — which is the
   * whole reason [D43](../../DECISIONS.md#d43) withheld the marks instead of releasing them.
   *
   * Cleared by the two actions that genuinely start a new attempt — Reset and Clear stored state —
   * and NOT by a mode switch, which re-mounts the same attempt rather than beginning another.
   */
  submitted: boolean;
}

export function newRecord(): HostRecord {
  return {
    mode: 'practice',
    feedbackMode: 'onAnswer',
    locale: 'he',
    state: null,
    responses: {},
    judgements: {},
    hintsUsed: [],
    checks: {},
    solutionsShown: [],
    released: {},
    verdicts: null,
    verdictsLocale: null,
    score: null,
    serverScore: null,
    serverParts: null,
    browserScore: null,
    submitted: false,
  };
}

/**
 * Which feedback timings each mode can coherently send. The first entry is what an incoherent
 * choice falls back to, and an EMPTY list means the host says nothing about timing at all.
 *
 * `mode` and `feedbackMode` are orthogonal in the contract (D42) because the *timing* of feedback
 * is a teaching decision. Not every combination is a decision, though, and offering the ones that
 * are not made this host lie:
 *
 *   exam    the scheme is withheld, so nothing in the frame can mark an answer as it arrives.
 *           "exam + onAnswer" told an asset marks were immediate while none could ever come, and
 *           `onCheck` says the same about a button. What remains is a real choice, and it is the
 *           host's to make: `onSubmit`, where the host scores at the submit that ends the attempt
 *           and releases the whole set (D49), or `none` — not marked for this learner in this
 *           session at all, submit included. The two differ in what the asset DRAWS (D46), which
 *           is why the host must be able to say which.
 *   review  nothing is answered and nothing is computed. A finished attempt is replayed out of
 *           the host's record, and the marks are part of that record exactly as `state` and
 *           `score` are — they arrive in `verdicts`. So there is no timing to choose and the host
 *           asserts none: `feedbackMode` is OMITTED, which the field is optional for.
 *   solution the answers are shown and nothing about a learner is. No responses, no marks, no
 *           score — so there is no timing here either, and the host asserts none for the same
 *           reason review does. What the asset draws comes from `init.solution` alone.
 *
 * Omitted rather than set to a value that sounds close. The SDK resolves an absent timing from
 * the payload it can see — `init.verdicts !== null` means the marks are released, so `onAnswer`;
 * no verdicts and no scheme means there are none to show, so `none` — and that is the same fact
 * read off the same data, decided once in the runtime instead of restated by every host. Sending
 * `onAnswer` here would be the host repeating something the payload already carries, under a name
 * that is wrong in review anyway: nothing is being answered. The contract has no word for
 * "released"; that gap is recorded in GAPS.md rather than papered over with the nearest value.
 */
export const FEEDBACK_BY_MODE: Record<Mode, readonly FeedbackMode[]> = {
  practice: ['onAnswer', 'onCheck', 'onSubmit', 'none'],
  exam: ['onSubmit', 'none'],
  review: [],
  solution: [],
};

export const FEEDBACK_LABEL: Record<FeedbackMode, string> = {
  onAnswer: 'on answer',
  onCheck: 'on check',
  onSubmit: 'on submit',
  none: 'none',
};

/**
 * What the host actually puts on the wire, or `null` for "the field is not sent".
 *
 * Shown as derived in a host's controls rather than silently overridden, because a menu whose
 * value is ignored looks broken.
 */
export function wireFeedbackMode(record: Pick<HostRecord, 'mode' | 'feedbackMode'>): FeedbackMode | null {
  const allowed = FEEDBACK_BY_MODE[record.mode];
  if (allowed.length === 0) return null;
  return allowed.includes(record.feedbackMode) ? record.feedbackMode : (allowed[0] as FeedbackMode);
}

/** Whether the Feedback menu has anything to offer, which review is the case it does not. */
export const feedbackIsChoosable = (mode: Mode): boolean => FEEDBACK_BY_MODE[mode].length > 0;

/**
 * A closed attempt is read-only, and `Init.canWrite` is the field that says so.
 *
 * Two ways an attempt is closed, and they are the same fact at different distances: it was
 * submitted here in this session, or it was submitted in an earlier one and this is the `review`.
 * `review` alone was hard-coded for months, which made the studio's review honest and its submit
 * a fiction — the attempt carried on accepting work after being graded, and that is precisely why
 * D43 withheld the marks rather than releasing them (D49).
 *
 * `canWrite` is advisory — the parent transport still puts a `saveState` on the wire, because an
 * asset must not need to know whether anyone is listening — so the HOST has to actually refuse,
 * which is what `onSave` and `onAnswer` consult this for. Nothing has to be re-mounted for a
 * submit to close the attempt; the refusal is the mechanism, and the flag is what a *later* mount
 * would carry.
 */
export const isReadOnly = (record: Pick<HostRecord, 'mode' | 'submitted'>): boolean =>
  record.mode === 'review' || record.mode === 'solution' || record.submitted;

/**
 * Which of the three it is, for the events log. "read-only" on its own sent people looking for a
 * mode switch they had not made, because the reason was usually the submit they just pressed.
 */
export const closedBecause = (record: Pick<HostRecord, 'mode'>): string => {
  if (record.mode === 'review') return 'review replays a finished attempt';
  if (record.mode === 'solution') return 'the solution is being shown, so there is nothing to answer';
  return 'this attempt was submitted, so it is closed';
};

/**
 * The locale actually sent, which is never one the asset did not declare.
 *
 * `manifest.locales` is a promise to the host about which languages exist in this asset, and a
 * real host picks from that list. Offering `he` and `en` to everything fails quietly rather than
 * loudly: `pick()` falls back to the alphabetically first locale present, so the CONTENT still
 * renders — in Hebrew — while `locale` and `dir` say English and left-to-right. The result is
 * Hebrew laid out LTR and announced to a screen reader as English, with nothing on screen to say
 * why. `declared` empty means the manifest said nothing, and Hebrew is the default.
 */
export function wireLocale(record: Pick<HostRecord, 'locale'>, declared: readonly string[]): string {
  const locales = declared.length > 0 ? declared : ['he'];
  return locales.includes(record.locale) ? record.locale : (locales[0] as string);
}

/**
 * The answers, when this host is showing them — the whole of solution mode on the host side.
 *
 * Derived from the scheme rather than stored beside it, so there is exactly one definition of
 * what the right answer is and a host cannot drift from what `/v1/score` marks correct. The
 * scheme itself still goes nowhere: `init.scheme` is null here, as it is in exam and review.
 *
 * `null` — the field not sent at all — whenever the mode is anything else, or when this host
 * never got hold of a scheme. The second case is the one worth being careful about: an empty
 * object would tell the asset a solution was released and every item happened to have no answer,
 * which is a lie that draws a blank screen. Not sending the field says the true thing.
 */
export function solutionForInit(
  record: Pick<HostRecord, 'mode'>,
  scheme: SchemeDoc | null,
): Record<string, ItemResponse> | null {
  if (record.mode !== 'solution') return null;
  if (scheme === null) return null;
  return solutionFor(scheme);
}

/**
 * A host obligation, demonstrated rather than described (D35).
 *
 * `message` is presentation: it was resolved for whichever locale was active when
 * the score was computed. Replaying it under a different locale shows Hebrew
 * feedback under English questions — which is exactly what the studio did until a
 * screenshot caught it, with every assertion passing.
 *
 * The facts survive a locale switch. The sentence does not.
 */
export function verdictsForInit(record: HostRecord, locale: string): ItemVerdict[] | null {
  if (record.verdicts === null && record.mode !== 'practice') return null;
  /**
   * A solution session is about the questions, not about a learner (D58) — so the marks this
   * host is holding stay here, even though it holds a full set.
   *
   * Worth stating rather than leaving to the guard below, because that guard would not have
   * caught it: it tests `wireFeedbackMode() === 'none'`, and solution mode asserts no timing at
   * all, so the value is `null` and the check passes straight through. Reaching this file after
   * a submit — which is the ordinary way to arrive here — would have shipped last attempt's
   * verdicts beside the answers, and the asset would have painted an attempt nobody made in this
   * session over the top of the solution.
   */
  if (record.mode === 'solution') return null;
  /**
   * `none` means this learner is not shown marks in this session, and a payload is a route like any
   * other (D49). The host stores the result either way — that is what `none` is *not* about — but
   * putting it in `init.verdicts` would hand it over at the next mount, and after a submit there
   * always is one (D50). The submit path checks the same thing before it reports; this is the check
   * that covers a mount arriving for any other reason, such as a locale switch.
   */
  if (wireFeedbackMode(record) === 'none') return null;
  if (record.verdicts === null) return releasedForInit(record, locale);
  if (record.verdictsLocale === locale) return record.verdicts;

  return record.verdicts.map(({ message: _stale, ...facts }) => facts);
}

/**
 * Before a submit: the marks this session already gave (D70), so a re-mounted question shows the
 * mark the learner saw — never one they did not. Only in practice that marks in-session
 * (`onAnswer`, `onCheck`); a message resolved in another locale is left out, as above.
 */
function releasedForInit(record: HostRecord, locale: string): ItemVerdict[] | null {
  const wire = wireFeedbackMode(record);
  if (record.mode !== 'practice' || (wire !== 'onAnswer' && wire !== 'onCheck')) return null;
  const marks = Object.values(record.released);
  if (marks.length === 0) return null;
  return marks.map(({ verdict, locale: given }) => {
    if (given === locale) return verdict;
    const { message: _stale, ...facts } = verdict;
    return facts;
  });
}

/**
 * The total, and the same exclusion `verdictsForInit` makes one field over.
 *
 * `score` is host chrome about an attempt. A solution session has no attempt in it, so a score
 * left over from the last one is a number about work this session is not showing — and an asset
 * that draws its own summary would draw it. Caught by the end-to-end suite rather than by
 * reasoning: the verdicts were excluded and the total beside them was not.
 */
export const scoreForInit = (record: HostRecord): Score | null =>
  record.mode === 'solution' ? null : record.score;

/**
 * Everything the host held about the ATTEMPT: the answers, the hints, the marks over them, and
 * the fact that it was submitted.
 *
 * Not `record.state`, which is the asset's own saved workings and outlives a reset — the asset
 * clears that itself as part of handling one. The two callers differ in exactly that one field,
 * which is why this is a function rather than two copies that drifted.
 */
export function clearAttempt(record: HostRecord): void {
  record.responses = {};
  record.judgements = {};
  record.hintsUsed = [];
  record.checks = {};
  record.solutionsShown = [];
  record.released = {};
  record.verdicts = null;
  record.verdictsLocale = null;
  record.score = null;
  record.serverScore = null;
  record.serverParts = null;
  record.browserScore = null;
  // Including the fact that an attempt was submitted, which is part of the record and not a
  // separate setting: nothing is left for it to be true of.
  record.submitted = false;
}

/**
 * One question out of the record: its answer, its marks and its hints. What a single-question
 * clear does on the host's side, before the asset clears the question on screen — the host has to
 * forget too, or a submit would score an answer the learner cleared.
 *
 * Only for an open attempt; the caller refuses a closed one (D49): clearing a scored answer would
 * rewrite the record.
 */
export function clearItem(record: HostRecord, itemId: string): void {
  const { [itemId]: _cleared, ...responses } = record.responses;
  record.responses = responses;
  const { [itemId]: _judged, ...judgements } = record.judgements;
  record.judgements = judgements;
  if (record.verdicts !== null) record.verdicts = record.verdicts.filter((verdict) => verdict.itemId !== itemId);
  record.hintsUsed = record.hintsUsed.filter((hint) => hint.itemId !== itemId);
  const { [itemId]: _checked, ...checks } = record.checks;
  record.checks = checks;
  record.solutionsShown = record.solutionsShown.filter((id) => id !== itemId);
  const { [itemId]: _released, ...released } = record.released;
  record.released = released;
}

/**
 * The offer `init.solutionAfter` carries, or `null` for none (D66).
 *
 * Only while the attempt is open and marks are given in this session — `onAnswer` or `onCheck`.
 * Under `onSubmit` or `none` a question is never checked, so a count to wait for would be a
 * promise nothing keeps; and a host holding no scheme has no answer to give.
 */
export function solutionOffer(
  record: Pick<HostRecord, 'mode' | 'feedbackMode' | 'submitted'>,
  after: number | null,
  scheme: SchemeDoc | null,
): number | null {
  if (after === null || !Number.isInteger(after) || after < 1) return null;
  if (scheme === null || isReadOnly(record)) return null;
  const feedback = wireFeedbackMode(record);
  return feedback === 'onAnswer' || feedback === 'onCheck' ? after : null;
}

/**
 * The limit `init.maxTries` carries, or `null` for none (D97): offered exactly where
 * `solutionOffer` is — an open attempt that marks in-session — since only there is a check counted.
 */
export const triesLimit = solutionOffer;
